import { memo, useCallback, useRef, useState } from 'react';
import { AddSegmentType } from './types';
import { updateClipData } from '../clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '../clipUpdateDataUtils/constants';
import Loader from '@/components/atoms/Loader';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import Icon from '@/components/atoms/Icon';
import { SegmentType } from '@/player-config/src/types/Segment/Type';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { getClipFPS } from '@/player-config/src/utils/clipUtils';
import { getClipTotalDurationInFrames } from '@/App/remotion/MainPlayer/utils/configUtils';

function VideoPreview({ url, type, onDelete }: { url: string; type: AddSegmentType; onDelete?: () => void }) {
  const { clipId } = useClipsContext();
  const [isLoaded, setIsLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { seekTo } = useRemotionPlayerSeek();
  function playVideoOnHover() {
    if (videoRef.current && videoRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      videoRef.current.play();
    }
  }

  function pauseVideo() {
    if (videoRef.current && videoRef.current.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      videoRef.current.pause();
    }
  }

  function updateIntroOutroValue() {
    if (isLoaded) {
      const newClipData = updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK,
        type: SegmentType.VIDEO,
        id: type === AddSegmentType.INTRO ? 'intro' : 'outro',
        url,
        duration: videoRef.current ? videoRef.current.duration : 0
      });
      if (type === AddSegmentType.INTRO) {
        seekTo(0);
      } else {
        const fps = getClipFPS(newClipData);
        seekTo(getClipTotalDurationInFrames(newClipData) / fps - (videoRef.current?.duration || 0) || 0);
      }
    }
  }

  function onVideoLoaded() {
    setIsLoaded(true);
  }

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (onDelete) {
        onDelete();
      }
    },
    [onDelete]
  );

  return (
    <div
      role="button"
      className="group relative aspect-video w-full cursor-pointer overflow-hidden rounded-lg border"
      data-testid={type === AddSegmentType.INTRO ? 'brand-intro-select-button' : 'brand-outro-select-button'}
      onMouseEnter={playVideoOnHover}
      onMouseLeave={pauseVideo}
      onClick={updateIntroOutroValue}
    >
      {onDelete && (
        <div
          className="absolute right-1 top-1 z-10 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          onClick={handleDelete}
        >
          <div className="rounded-full bg-black/50 p-1">
            <Icon name="IconTrash" width={16} height={16} color="white" />
          </div>
        </div>
      )}
      <video className="absolute" ref={videoRef} muted onCanPlay={onVideoLoaded}>
        <source src={url} type="video/mp4" />
      </video>
      {!isLoaded && (
        <div className="absolute inset-1/2 h-8 w-[40px] -translate-x-1/2 -translate-y-1/2">
          <Loader />
        </div>
      )}
    </div>
  );
}

export default memo(VideoPreview);
